import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Switch } from '@fluentui/react-components';
import { DismissRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { type Page, type PageProps, useDialogFocus, formatCompactNumber } from '../workbench-shared';
import { useViewState } from '../ui/session-state';
import { NumberInput } from '../ui/controls';
import { flushInputDrafts } from '../ui/input-drafts';
import { planIssues, needsReference } from '../plan-readiness';
import { MetricsEditor } from './metrics-editor';
import { ResourcesPage } from './resources-page';

type ConfigPanel = 'base' | 'objectives' | 'solver';
export function StudyWorkspace(props: PageProps & { section: Page }) {
  const { userPlan, workbench, setUserPlan, section } = props;
  const root = useRef<HTMLDivElement>(null);
  const requestedField = useRef<string | undefined>(undefined);
  const previousSection = useRef(section);
  // Read the existing per-plan preference; objectives and solver are now always visible.
  const [panel, setPanel] = useViewState<ConfigPanel | null>('workspace.config', null);
  const [folded,setFolded] = useViewState('workspace.configFolded',false);
  const referenceOpen = panel === 'base';
  const close = () => { flushInputDrafts(); setPanel(null); };
  const panelRef = useDialogFocus(referenceOpen, close);
  useLayoutEffect(() => {
    if (!referenceOpen) return;
    const inputs = Array.from(panelRef.current?.querySelectorAll<HTMLInputElement>('input') ?? []);
    const preferred = inputs.find(input => input.getAttribute('aria-label') === requestedField.current) ?? inputs[0];
    // Let the dialog's focus manager select the validation field on its first frame.
    if (preferred) preferred.dataset.dialogAutofocus = 'true';
    return () => { if (preferred) delete preferred.dataset.dialogAutofocus; };
  }, [referenceOpen]);
  const issues = planIssues(workbench, userPlan);
  const referenceIssues = issues.filter(issue => issue.section === 'base');
  const configure = (patch: Partial<typeof userPlan.solver>) => setUserPlan(plan => ({ ...plan, solver: { ...plan.solver, ...patch } }));
  const open = (next: ConfigPanel | 'resources', inputLabel?: string) => {
    if(next !== 'resources') setFolded(false);
    requestedField.current = inputLabel;
    flushInputDrafts(); setPanel(next === 'base' ? 'base' : null);
    requestAnimationFrame(() => {
      const area = next === 'base' ? panelRef.current : root.current?.querySelector<HTMLElement>(`[data-config-section="${next}"]`);
      const input = inputLabel ? Array.from(area?.querySelectorAll<HTMLInputElement>('input') ?? []).find(input => input.getAttribute('aria-label') === inputLabel) : null;
      const target = input ?? area;
      target?.focus({ preventScroll: true }); target?.scrollIntoView({ block: 'nearest' });
    });
  };
  useEffect(() => {
    if (section === 'objectives' || section === 'base' || section === 'solver') open(section);
    if (section === 'resources') {
      if (previousSection.current !== section) open('resources');
      else close();
    }
    previousSection.current = section;
  }, [section]);
  useEffect(() => {
    const focusIssue = (event: Event) => { const issue = (event as CustomEvent).detail; open(issue.section, issue.inputLabel); };
    window.addEventListener('minglun:plan-issue', focusIssue);
    return () => window.removeEventListener('minglun:plan-issue', focusIssue);
  });
  const referenceSummary = workbench.optimizationMetrics.filter(needsReference).slice(0,2).map(metric=>`${metric.name} ${formatCompactNumber(userPlan.baseAttributes.find(item=>item.metricId===metric.id)?.value ?? 0)}`).join(' · ');
  return <div ref={root} className={`study-workspace grouped-workspace ${folded?'config-is-folded':''}`}>
    <div className="config-fold-bar"><span>{folded?`${userPlan.objectives.filter(x=>x.weight>0).length} 项偏好 · SSR 自选 ${userPlan.solver.ssrWheelChoices}`:'优化偏好与计算选项'}</span><button type="button" aria-expanded={!folded} onClick={()=>{flushInputDrafts();setFolded(old=>!old);}}>{folded?'展开配置':'收起配置'}<ChevronRightRegular/></button></div>
    <div className="priority-config">
      <section className="objective-config" aria-label="优化偏好" data-config-section="objectives" tabIndex={-1}>
        <MetricsEditor {...props} mode="objectives" heading="优化偏好" />
        {issues.some(issue => issue.section === 'objectives') && <div className="config-issues" role="status">{issues.filter(issue => issue.section === 'objectives').map(issue => <button type="button" key={issue.inputLabel} onClick={() => open('objectives', issue.inputLabel)}>{issue.label}</button>)}</div>}
        <button type="button" className="reference-trigger" aria-haspopup="dialog" aria-expanded={referenceOpen} onClick={() => open('base')}>
          <span>角色参考属性</span><span className={referenceIssues.length ? 'config-warning' : ''}>{referenceIssues.length ? `${referenceIssues.length} 项待填写` : referenceSummary}</span><ChevronRightRegular />
        </button>
      </section>
      <section className="priority-solver" aria-label="计算选项" data-config-section="solver" tabIndex={-1}>
        <header><h2>计算选项</h2><label className="compact-ssr"><span>SSR 自选数量</span><NumberInput value={userPlan.solver.ssrWheelChoices} min={0} ariaLabel="SSR 命轮自选数量" onChange={ssrWheelChoices=>configure({ssrWheelChoices})} /></label></header>
        <div className="calculation-options">
          <div><Switch label="宿命之轮" labelPosition="before" checked={userPlan.solver.includeDestinyWheel} onChange={(_,data) => configure({includeDestinyWheel:data.checked})} /><p>将宿命之轮纳入计算。</p></div>
          <div><Switch label="SSR 自选均衡" labelPosition="before" checked={userPlan.solver.ssrChoiceBalanceEnabled} disabled={userPlan.solver.ssrWheelChoices <= 0} onChange={(_,data) => configure({ssrChoiceBalanceEnabled:data.checked})} /><p>{userPlan.solver.ssrWheelChoices <= 0 ? '自选数量为 0，均衡暂不生效。' : '分配自选时兼顾后续培养空间。'}</p></div>
          <div><Switch label="碎片分析" labelPosition="before" checked={userPlan.solver.fragmentAnalysisEnabled} onChange={(_,data) => configure({fragmentAnalysisEnabled:data.checked})} /><p>分析碎片缺口与对应收益。</p></div>
        </div>
      </section>
    </div>
    {referenceOpen && createPortal(<div className="reference-layer">
      <button type="button" className="config-backdrop" aria-label="关闭配置" onClick={close} />
      <aside ref={panelRef} className="config-shelf reference-dialog" role="dialog" aria-modal="true" aria-label="角色参考属性" tabIndex={-1}>
        <header><h2>角色参考属性</h2><button type="button" aria-label="关闭配置" onClick={close}><DismissRegular /></button></header>
        {referenceIssues.length > 0 && <div className="config-issues" role="status">{referenceIssues.map(issue => <button type="button" key={issue.inputLabel} onClick={() => open('base', issue.inputLabel)}>{issue.label}</button>)}</div>}
        <MetricsEditor {...props} mode="base" />
      </aside>
    </div>, document.querySelector('.app-frame') ?? document.body)}
    <ResourcesPage {...props} />
  </div>;
}
